import React from 'react';
import { CloudOff, RefreshCw } from 'lucide-react';
import { useSyncStatus, openSyncCenter } from '../../../../hooks/useSyncStatus';
import { useOnlineStatus } from '../../../../hooks/useOfflineDevice';
import { OfflineMode } from '../../../../services/offline/offlineMode';
import { RECONNECT_MESSAGE } from '../../../../services/offline/network';

/**
 * Sync state of the visit's work (Phase 16C C8; replaces the Phase 9B draft chip).
 * - Field mode (enrolled phone or tablet): "N items not synced" for visits, entries and
 *   assessments, with Sync now and Sync Center; conflicts and held items are called out.
 * - Laptop, or a phone without offline mode: nothing is stored; offline shows the reconnect message.
 */
const DraftStatusChip: React.FC<{ visitId?: string; onSynced?: () => void }> = ({ visitId, onSynced }) => {
  const online = useOnlineStatus();
  const status = useSyncStatus(visitId);
  const previous = React.useRef(status.forVisit);

  React.useEffect(() => {
    if (previous.current > 0 && status.forVisit < previous.current) onSynced?.();
    previous.current = status.forVisit;
  }, [status.forVisit, onSynced]);

  if (!OfflineMode.enabled()) {
    if (online) return null;
    return (
      <div role="status" className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <CloudOff size={16} className="shrink-0" aria-hidden="true" />
        <span>{RECONNECT_MESSAGE} Unsaved text stays in the form until you leave this page.</span>
      </div>
    );
  }

  const count = status.forVisit;
  if (count === 0 && online) return null;
  const problems = (visitId ? status.ops.filter((o) => o.visitId === visitId) : status.ops)
    .filter((o) => o.status === 'conflict' || o.status === 'needs_attention').length;
  return (
    <div role="status" aria-live="polite" className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm ${problems ? 'border-red-200 bg-red-50 text-red-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
      <CloudOff size={16} className="shrink-0" aria-hidden="true" />
      <span className="flex-1">
        {count === 0
          ? 'Offline. New work is saved encrypted on this device and sent when you\'re back online.'
          : `${count} item${count === 1 ? '' : 's'} not synced${problems ? ` · ${problems} need${problems === 1 ? 's' : ''} your attention` : ''} — saved encrypted on this device.`}
      </span>
      {count > 0 && online && (
        <button type="button" onClick={() => status.retry()} disabled={status.engine.syncing} className="flex min-h-[40px] items-center gap-1 rounded-md border border-current bg-white px-3 text-sm font-medium">
          <RefreshCw size={14} className={status.engine.syncing ? 'animate-spin' : ''} aria-hidden="true" />{status.engine.syncing ? 'Syncing…' : 'Sync now'}
        </button>
      )}
      {count > 0 && (
        <button type="button" onClick={openSyncCenter} className="min-h-[40px] rounded-md border border-current bg-white px-3 text-sm font-medium">Sync Center</button>
      )}
    </div>
  );
};

export default DraftStatusChip;
